<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useCartStore } from '@/stores/cart';
import { formatDollars } from '@/utils/format';

const auth = useAuthStore();
const cart = useCartStore();
const route = useRoute();
const router = useRouter();

const balance = computed(() =>
  auth.user ? formatDollars(auth.user.walletAvailableCents) : '$0.00',
);

async function handleLogout() {
  drawerOpen.value = false;
  await auth.logout();
  router.push({ name: 'Login' });
}

// Mobile burger drawer state. Close on route change so navigation feels
// natural; the drawer itself nav-links call drawerOpen=false on click.
const drawerOpen = ref(false);
watch(() => route.fullPath, () => { drawerOpen.value = false; });

// Public-folder asset paths (bound dynamically so Vite serves them at runtime)
const a = (name: string) => `/assets/header/${name}`;

interface NavItem {
  to: string;
  label: string;
  icon: string;
  matchPath?: string; // path prefix that lights this nav as active
  disabled?: boolean;
}

// Cart is a button that opens the popover, rendered separately below.
const navItems: NavItem[] = [
  { to: '/',          label: 'home',        icon: a('nav-home.svg'),        matchPath: '/' },
  { to: '/catalog',   label: 'marketplace', icon: a('nav-marketplace.svg'), matchPath: '/catalog' },
  { to: '/blog',      label: 'blog',        icon: a('nav-blog.svg'),        matchPath: '/blog' },
  { to: '/about',     label: 'about us',    icon: a('nav-about.svg'),       matchPath: '/about' },
  { to: '/faq',       label: 'faq',         icon: a('nav-faq.svg'),         matchPath: '/faq' },
];

const navTrailingItems: NavItem[] = [
  { to: '/favorites', label: 'favorites', icon: a('nav-favorites.svg'), matchPath: '/favorites' },
];

const cartIcon = a('nav-cart.svg');

function isActive(item: NavItem): boolean {
  if (!item.matchPath) return false;
  if (item.matchPath === '/') return route.path === '/';
  return route.path === item.matchPath || route.path.startsWith(item.matchPath + '/');
}
</script>

<template>
  <header class="bg-page text-white">
    <!-- Top bar: burger (mobile) → logo → greeting (md+) → wallet → profile → logout (md+) -->
    <div class="max-w-[1504px] mx-auto px-4 lg:px-8 pt-4 lg:pt-6 pb-3 flex items-center gap-3 lg:gap-6">
      <!-- Mobile burger — only visible < md. Opens the drawer. -->
      <button
        type="button"
        class="md:hidden w-10 h-10 rounded-[8px] bg-[#161920] flex items-center justify-center text-white border-0 cursor-pointer"
        aria-label="Open menu"
        @click="drawerOpen = true"
      >
        <svg width="20" height="14" viewBox="0 0 20 14" fill="none" aria-hidden="true">
          <path d="M2 2H18M2 7H18M2 12H18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>

      <RouterLink
        to="/"
        class="flex items-center no-underline"
        aria-label="Epic Cherry home"
      >
        <img
          src="/assets/brand/logo.svg"
          alt="Epic Cherry"
          class="h-16 lg:h-[120px] w-auto select-none"
        />
      </RouterLink>

      <!-- Greeting (auth only, md+) -->
      <p
        v-if="auth.user"
        class="font-display text-2xl lg:text-[40px] uppercase leading-none hidden md:block"
      >
        <span class="text-[var(--color-text-faded)]">good evening,</span>
        <span class="text-white">&nbsp;{{ auth.user.username }}</span>
      </p>

      <!-- Right side -->
      <div class="ml-auto flex items-center gap-3 lg:gap-6">
        <!-- Skeleton while auth state is resolving -->
        <template v-if="!auth.initialized">
          <div class="flex items-center gap-3 animate-pulse">
            <div class="h-[38px] w-[72px] rounded-lg bg-[var(--color-surface)]" />
            <div class="h-[38px] w-[88px] rounded-lg bg-[var(--color-surface)]" />
          </div>
        </template>

        <template v-else-if="auth.user">
          <!-- Balance -->
          <RouterLink
            to="/wallet"
            class="font-display uppercase text-[13px] lg:text-lg text-white no-underline hover:text-[var(--color-accent)] flex items-center gap-2 lg:gap-3 transition-colors"
          >
            <span class="hidden md:inline">EUR</span>
            <img :src="a('chevron-down.svg')" alt="" class="w-2 h-2 opacity-70 hidden md:inline" />
            <img :src="a('balance-button.svg')" alt="" class="h-4 lg:h-5 w-auto" />
            <span>{{ balance }}</span>
          </RouterLink>

          <!-- Profile -->
          <RouterLink
            to="/profile"
            class="font-display uppercase text-base lg:text-lg text-white no-underline hover:text-[var(--color-accent)] flex items-center gap-2 transition-colors"
          >
            <img
              v-if="auth.user.avatarUrl"
              :src="auth.user.avatarUrl"
              :alt="auth.user.username"
              class="w-6 h-6 rounded-full ring-1 ring-[var(--color-border)]"
            />
            <img v-else :src="a('profile.svg')" alt="" class="w-[22px] h-[22px]" />
            <span class="hidden md:inline">profile</span>
          </RouterLink>

          <!-- Log out — desktop only; mobile uses the burger drawer's red entry -->
          <button
            class="hidden md:inline-flex font-display uppercase text-base lg:text-lg text-white hover:text-[var(--color-accent)] items-center gap-2 bg-transparent border-0 p-0 transition-colors"
            @click="handleLogout"
          >
            <span
              class="w-[22px] h-[22px] bg-current inline-block"
              :style="{
                maskImage: `url('${a('logout.png')}')`,
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskImage: `url('${a('logout.png')}')`,
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
              }"
              aria-hidden="true"
            />
            <span>log out</span>
          </button>
        </template>

        <template v-else>
          <RouterLink
            to="/login"
            class="font-display uppercase text-[13px] lg:text-lg text-white no-underline border border-[var(--color-border)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] px-4 py-2 lg:px-5 lg:py-2.5 rounded-lg transition-colors"
          >
            log in
          </RouterLink>
          <RouterLink
            to="/login?mode=signup"
            class="font-display uppercase text-[13px] lg:text-lg text-white no-underline bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] hover:text-white px-4 py-2 lg:px-5 lg:py-2.5 rounded-lg transition-colors"
          >
            sign up
          </RouterLink>
        </template>
      </div>
    </div>

    <!-- Desktop nav pill — hidden on mobile, the burger drawer carries these links there. -->
    <div class="max-w-[1504px] mx-auto px-4 lg:px-8 pb-6 hidden md:block">
      <nav
        class="relative bg-[#161920] rounded-[17px] h-[79px] flex items-stretch justify-center mx-auto px-2 max-w-[1144px]"
      >
        <RouterLink
          v-for="item in navItems"
          :key="item.label"
          :to="item.disabled ? '' : item.to"
          :tabindex="item.disabled ? -1 : 0"
          :aria-disabled="item.disabled || undefined"
          class="relative flex flex-col items-center justify-center gap-1.5 px-4 lg:px-6 shrink-0 no-underline transition-colors"
          :class="[
            item.disabled
              ? 'text-[var(--color-text-faded)] cursor-not-allowed pointer-events-none'
              : 'text-white hover:text-[var(--color-accent)]',
          ]"
        >
          <span
            v-if="isActive(item) && !item.disabled"
            class="absolute inset-y-2 inset-x-1 rounded-[14px] bg-[#201E2C]"
            aria-hidden="true"
          />
          <img
            :src="item.icon"
            alt=""
            aria-hidden="true"
            class="relative z-10 h-[22px] w-auto"
            :class="item.disabled ? 'opacity-50' : ''"
          />
          <span class="relative z-10 font-display uppercase text-[15px] lg:text-[17px] leading-none whitespace-nowrap">
            {{ item.label }}
          </span>
        </RouterLink>

        <!-- Cart button (toggles the popover; carries an item-count badge) -->
        <button
          type="button"
          aria-label="Open cart"
          class="relative flex flex-col items-center justify-center gap-1.5 px-4 lg:px-6 shrink-0 text-white hover:text-[var(--color-accent)] transition-colors bg-transparent border-0 cursor-pointer"
          @click="cart.toggle()"
        >
          <span
            v-if="cart.open"
            class="absolute inset-y-2 inset-x-1 rounded-[14px] bg-[#201E2C]"
            aria-hidden="true"
          />
          <span class="relative z-10">
            <img :src="cartIcon" alt="" aria-hidden="true" class="h-[22px] w-auto" />
            <span
              v-if="cart.count > 0"
              class="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--color-accent)] text-white font-display text-[10px] leading-[18px] text-center"
            >
              {{ cart.count }}
            </span>
          </span>
          <span class="relative z-10 font-display uppercase text-[15px] lg:text-[17px] leading-none whitespace-nowrap">
            cart
          </span>
        </button>

        <RouterLink
          v-for="item in navTrailingItems"
          :key="item.label"
          :to="item.disabled ? '' : item.to"
          :tabindex="item.disabled ? -1 : 0"
          :aria-disabled="item.disabled || undefined"
          class="relative flex flex-col items-center justify-center gap-1.5 px-4 lg:px-6 shrink-0 no-underline transition-colors"
          :class="[
            item.disabled
              ? 'text-[var(--color-text-faded)] cursor-not-allowed pointer-events-none'
              : 'text-white hover:text-[var(--color-accent)]',
          ]"
        >
          <img
            :src="item.icon"
            alt=""
            aria-hidden="true"
            class="relative z-10 h-[22px] w-auto"
            :class="item.disabled ? 'opacity-50' : ''"
          />
          <span class="relative z-10 font-display uppercase text-[15px] lg:text-[17px] leading-none whitespace-nowrap">
            {{ item.label }}
          </span>
        </RouterLink>
      </nav>
    </div>

    <!-- Mobile drawer: slides in from the left. Teleported to body so it
         sits on top of everything regardless of where AppHeader is mounted.
         Backdrop closes it; route changes also auto-close (see watcher). -->
    <Teleport to="body">
      <div v-if="drawerOpen" class="md:hidden fixed inset-0 z-[60]" role="dialog" aria-label="Menu">
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/70"
          @click="drawerOpen = false"
        />
        <!-- Panel -->
        <aside
          class="absolute left-0 top-0 bottom-0 w-[78vw] max-w-[320px] bg-[#161920] flex flex-col shadow-2xl"
        >
          <!-- Drawer header -->
          <div class="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[var(--color-border)]">
            <img src="/assets/brand/logo.svg" alt="Epic Cherry" class="h-10 w-auto select-none" />
            <button
              type="button"
              class="w-10 h-10 rounded-[8px] bg-transparent text-white text-[24px] leading-none border-0 cursor-pointer"
              aria-label="Close menu"
              @click="drawerOpen = false"
            >
              ×
            </button>
          </div>

          <!-- Nav links -->
          <nav class="flex-1 overflow-y-auto py-2">
            <RouterLink
              v-for="item in [...navItems, ...navTrailingItems]"
              :key="item.label"
              :to="item.disabled ? '' : item.to"
              :tabindex="item.disabled ? -1 : 0"
              :aria-disabled="item.disabled || undefined"
              class="flex items-center gap-3 px-5 py-3.5 no-underline transition-colors"
              :class="[
                item.disabled
                  ? 'text-[var(--color-text-faded)] pointer-events-none'
                  : isActive(item)
                    ? 'text-[var(--color-accent)] bg-[#201E2C]'
                    : 'text-white hover:bg-[#1c1f28]',
              ]"
            >
              <img :src="item.icon" alt="" aria-hidden="true" class="h-[20px] w-auto" />
              <span class="font-display uppercase text-[15px] leading-none">{{ item.label }}</span>
            </RouterLink>

            <!-- Cart entry (opens the popover, closes the drawer) -->
            <button
              type="button"
              class="w-full flex items-center gap-3 px-5 py-3.5 text-white hover:bg-[#1c1f28] bg-transparent border-0 cursor-pointer"
              @click="drawerOpen = false; cart.toggle()"
            >
              <span class="relative h-[20px]">
                <img :src="cartIcon" alt="" aria-hidden="true" class="h-[20px] w-auto" />
                <span
                  v-if="cart.count > 0"
                  class="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--color-accent)] text-white font-display text-[10px] leading-[16px] text-center"
                >
                  {{ cart.count }}
                </span>
              </span>
              <span class="font-display uppercase text-[15px] leading-none">cart</span>
            </button>
          </nav>

          <!-- Logout (red) — only when logged in. Lives at the bottom so it's
               last in the mental order (look here / nav / leave). -->
          <div
            v-if="auth.user"
            class="border-t border-[var(--color-border)] p-3"
          >
            <button
              type="button"
              class="w-full flex items-center gap-3 px-3 py-3 rounded-[8px] bg-transparent text-[var(--color-error)] hover:bg-[var(--color-error)]/10 font-display uppercase text-[15px] leading-none border-0 cursor-pointer"
              @click="handleLogout"
            >
              <span
                class="w-[20px] h-[20px] bg-current inline-block"
                :style="{
                  maskImage: `url('${a('logout.png')}')`,
                  maskSize: 'contain',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  WebkitMaskImage: `url('${a('logout.png')}')`,
                  WebkitMaskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                }"
                aria-hidden="true"
              />
              log out
            </button>
          </div>
        </aside>
      </div>
    </Teleport>
  </header>
</template>
